import JSZip from 'jszip';

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const downloadLink = document.createElement('a');
  downloadLink.href = url;
  downloadLink.download = filename;
  downloadLink.click();
}

// O `click()` só inicia o download: revogar a URL na hora pode cortar o arquivo em alguns
// navegadores. Um minuto basta para o navegador assumir o blob; sem revogar, cada download
// (até ~1,4 GB no caminho reserva do Flux) ocupava a memória da aba até ela fechar.
export const OBJECT_URL_REVOKE_DELAY_MS = 60_000;

/** Entrega um blob já baixado ao navegador e libera a memória depois (#1955). */
export function saveBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), OBJECT_URL_REVOKE_DELAY_MS);
}

/** URL absoluta da API para um caminho relativo devolvido pelo backend (ex.: `/produtos/...`). */
export function apiUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  const base = (process.env.REACT_APP_AMPERE_API_URL ?? '').replace(/\/+$/, '');
  return `${base}/${path.replace(/^\/+/, '')}`;
}

/**
 * Download nativo: o navegador grava direto no disco e mostra a própria barra de progresso,
 * sem o arquivo passar pela memória da página. O nome vem do `Content-Disposition` do servidor
 * (o atributo `download` é ignorado em link de outra origem). Mesmo padrão do Windx.
 */
export function downloadFromUrl(url: string) {
  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener noreferrer';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

async function fileFromUrl(url: string, filename: string) {
  const blob = await fetch(url).then((response) => response.blob());
  return new File([blob], filename);
}

export function base64ToBlob(b64File: string, type?: string) {
  const blobData = atob(b64File);
  const u8arr = new Uint8Array(blobData.length);
  for (let i = 0; i < blobData.length; i += 1) {
    u8arr[i] = blobData.charCodeAt(i);
  }
  return new Blob([u8arr], type ? { type } : undefined);
}

export default function downloadFile(b64File: string, filename: string) {
  downloadBlob(base64ToBlob(b64File), filename);
}

export async function downloadZip(
  files: { content: string, filename: string }[],
  zipFilename: string,
) {
  const zip = new JSZip();
  files.forEach((file) => {
    zip.file(file.filename, file.content, { base64: true });
  });

  return zip
    .generateAsync({ type: 'blob' })
    .then((blob) => {
      downloadBlob(blob, zipFilename);
    });
}

export async function downloadZipFromUrls(
  fileData: { url: string, filename: string }[],
  zipFilename: string,
) {
  const zip = new JSZip();
  const files = await Promise.all(
    fileData.map(({ url, filename }) => fileFromUrl(url, filename)),
  );
  files.forEach((file, i) => {
    zip.file(fileData[i].filename, file);
  });

  return zip
    .generateAsync({ type: 'blob' })
    .then((blob) => {
      downloadBlob(blob, zipFilename);
    });
}
